import { APP_DISPLAY_NAME, loginRequestSchema, totpCodeSchema } from '@gb-ticket/shared-types';
import { ChevronDownIcon, EyeIcon, EyeOffIcon, Loader2Icon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { type FormEvent, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { microsoftLoginHref, ssoErrorMessage } from '@/platform/auth/microsoft-login';
import { useEnableTotp, useLogin } from '@/platform/auth/use-auth-mutations';
import { useMe } from '@/platform/auth/use-me';
import { useSsoStatus } from '@/platform/auth/use-sso-status';
import {
  brandDisplayName,
  isTenantSuspendedBrandError,
  publicBrandLogoSrc,
  usePublicBrand,
} from '@/platform/brand/use-public-brand';
import { resolvePostLoginPath } from '@/platform/lib/post-login';
import { START_PATH } from '@/platform/lib/products';
import { usePageTitle } from '@/hooks/use-page-title';
import { usePresentVoiceText, useVoiceCopy } from '@/platform/voice/use-voice';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

function LoginBrandHeader() {
  const brand = usePublicBrand();
  const name = brandDisplayName(brand.data);
  const logoSrc = publicBrandLogoSrc(brand.data?.logoUrl);

  return (
    <div className="overflow-hidden rounded-lg bg-brand-navy-deep px-6 py-8 text-center">
      {logoSrc ? (
        <img src={logoSrc} alt={name || APP_DISPLAY_NAME} className="mx-auto h-16 w-auto sm:h-20" />
      ) : name ? (
        <p className="text-lg font-semibold text-white">{name}</p>
      ) : null}
      <p className="mt-4 text-sm font-medium text-white/90">{APP_DISPLAY_NAME}</p>
      <p className="mt-1 text-xs text-white/80">Interne IT · Anmeldung</p>
    </div>
  );
}

export function LoginPage() {
  usePageTitle('Anmelden');
  const copy = useVoiceCopy();
  const present = usePresentVoiceText();
  const me = useMe();
  const brand = usePublicBrand();
  const login = useLogin();
  const enableTotp = useEnableTotp();
  const sso = useSsoStatus();
  const [params] = useSearchParams();
  const from = params.get('from') ?? START_PATH;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [setup, setSetup] = useState<{
    setupToken: string;
    secret: string;
    otpauthUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(() => ssoErrorMessage(params.get('error')));
  const [passwordLoginOpen, setPasswordLoginOpen] = useState(false);
  const ssoConfigured = sso.data?.configured === true;
  const showMicrosoft = ssoConfigured && !setup && !needsTotp;
  const showPasswordForm =
    needsTotp || !!setup || passwordLoginOpen || (sso.isFetched && !ssoConfigured);

  if (me.data) {
    return <Navigate to={resolvePostLoginPath(me.data, from)} replace />;
  }

  if (brand.isError && isTenantSuspendedBrandError(brand.error)) {
    return (
      <div className="mx-auto w-full max-w-md">
        <LoginBrandHeader />
        <p role="alert" className="mt-6 text-center text-sm text-destructive">
          {brand.error.message}
        </p>
      </div>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (setup) {
        const parsed = totpCodeSchema.safeParse(totpCode);
        if (!parsed.success) {
          setError('platform.auth.login.code');
          return;
        }
        const result = await enableTotp.mutateAsync({
          totpCode: parsed.data,
          setupToken: setup.setupToken,
          rememberMe,
        });
        if (result.status !== 'ok') {
          setError('Zwei-Faktor-Authentifizierung konnte nicht aktiviert werden.');
        }
        return;
      }

      const parsed = loginRequestSchema.safeParse({
        email,
        password,
        totpCode: needsTotp ? totpCode : undefined,
        rememberMe,
      });
      if (!parsed.success) {
        setError(needsTotp ? 'platform.auth.login.code' : 'platform.auth.login.credentials');
        return;
      }
      const result = await login.mutateAsync(parsed.data);
      if (result.status === 'totp_required') {
        setNeedsTotp(true);
        setTotpCode('');
        setError(null);
        return;
      }
      if (result.status === 'totp_setup_required') {
        setSetup({
          setupToken: result.setupToken,
          secret: result.secret,
          otpauthUrl: result.otpauthUrl,
        });
        setTotpCode('');
        setError(null);
      }
    } catch (err) {
      if (err instanceof ApiRequestError && err.error.code === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        setTotpCode('');
        setError(null);
        return;
      }
      setError(err instanceof ApiRequestError ? err.message : 'Anmeldung fehlgeschlagen.');
    }
  }

  const pending = login.isPending || enableTotp.isPending;

  function startMicrosoftLogin() {
    window.location.assign(microsoftLoginHref(API_BASE_URL, from));
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6">
      <LoginBrandHeader />

      <Card className="w-full shadow-none">
        <CardHeader>
          <CardTitle>Willkommen zurück</CardTitle>
          <CardDescription>
            {copy(
              setup
                ? 'platform.auth.login.totp-admin'
                : needsTotp
                  ? 'platform.auth.login.totp-confirmed'
                  : ssoConfigured
                    ? 'platform.auth.login.microsoft'
                    : 'platform.auth.login.helpdesk',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {present(error)}
            </p>
          )}
          {showMicrosoft && (
            <div className="space-y-4">
              <Button type="button" className="w-full" onClick={startMicrosoftLogin}>
                Mit Microsoft 365 anmelden
              </Button>
              <button
                type="button"
                className="flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                aria-expanded={passwordLoginOpen}
                aria-controls="password-login"
                onClick={() => setPasswordLoginOpen((open) => !open)}
              >
                oder mit E-Mail und Passwort
                <ChevronDownIcon
                  className={cn('size-3.5 transition-transform', passwordLoginOpen && 'rotate-180')}
                  aria-hidden
                />
              </button>
            </div>
          )}
          {showPasswordForm && (
            <form
              id="password-login"
              className="space-y-4"
              onSubmit={(event) => void onSubmit(event)}
            >
              {setup && (
                <div className="space-y-3 rounded-md bg-muted p-3 text-sm">
                  <div className="flex justify-center">
                    <div className="rounded-md bg-white p-3">
                      <QRCodeSVG
                        value={setup.otpauthUrl}
                        size={180}
                        level="M"
                        marginSize={4}
                        title="QR-Code zum Einrichten der Zwei-Faktor-Authentifizierung"
                      />
                    </div>
                  </div>
                  <p className="text-center text-xs text-muted-foreground">
                    App kann den Code nicht scannen? Schlüssel manuell eingeben:
                  </p>
                  <p className="text-center font-mono break-all">{setup.secret}</p>
                  <p className="text-center">
                    <a className="text-primary underline" href={setup.otpauthUrl}>
                      In Authenticator-App öffnen
                    </a>
                  </p>
                </div>
              )}
              {!setup && !needsTotp && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Passwort</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="absolute top-1/2 right-1 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-pressed={showPassword}
                        aria-controls="password"
                        aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      >
                        {showPassword ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
                      </Button>
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">Angemeldet bleiben</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Auch nach dem Schließen des Browsers auf diesem Gerät angemeldet bleiben.
                      </span>
                    </span>
                  </label>
                </>
              )}
              {(needsTotp || setup) && (
                <div className="space-y-1.5">
                  <Label htmlFor="totp">Authenticator-Code</Label>
                  <Input
                    id="totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    value={totpCode}
                    onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ''))}
                    required
                  />
                </div>
              )}
              <Button
                type="submit"
                variant={showMicrosoft ? 'outline' : 'default'}
                className="w-full"
                disabled={pending}
              >
                {pending && <Loader2Icon className="animate-spin" aria-hidden />}
                {setup
                  ? '2FA aktivieren und anmelden'
                  : needsTotp
                    ? 'Code prüfen und anmelden'
                    : 'Anmelden'}
              </Button>
              {needsTotp && !setup && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setNeedsTotp(false);
                    setTotpCode('');
                    setError(null);
                  }}
                >
                  Zurück zur Passwort-Eingabe
                </Button>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
